/**
 * @file Duplicate upload files API service.
 * @description List duplicate lead upload files and download by filename.
 */
import { apiFetch } from '../api/api';
import { API_ENDPOINTS } from '../constants/api';
import { buildQueryString } from '../utils/api';
import { LeadsExportService } from './leadsExportService';
import type {
  DuplicateFile,
  DuplicateFilesListResult,
  DuplicateFileDownloadResult,
} from '../types/api/duplicateFiles';

export type {
  DuplicateFile,
  DuplicateFilesListResult,
  DuplicateFilesPagination,
  DuplicateFileDownloadResult,
} from '../types/api/duplicateFiles';

function strField(value: unknown, fallback = ''): string {
  if (value == null || value === '') return fallback;
  return String(value);
}

function rowRecord(row: unknown): Record<string, unknown> {
  return row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
}

function normalizeRow(row: unknown, index: number): DuplicateFile | null {
  if (typeof row === 'string' || typeof row === 'number') {
    const filename = String(row).trim();
    if (!filename) return null;
    return { id: filename, filename };
  }

  const r = rowRecord(row);
  const filename = strField(
    r['file_name'] ?? r['filename'] ?? r['original_filename'] ?? r['name'],
  );
  const id = strField(r['id']) || filename || `duplicate-${index}`;
  if (!filename) return null;

  return { id, filename };
}

function extractRows(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];

  const root = raw as Record<string, unknown>;
  const data = root['data'];

  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['files'])) return inner['files'];
    if (Array.isArray(inner['data'])) return inner['data'];
    if (Array.isArray(inner['duplicates'])) return inner['duplicates'];
    if (Array.isArray(inner['duplicate_files'])) return inner['duplicate_files'];
  }
  if (Array.isArray(root['files'])) return root['files'];
  if (Array.isArray(root['duplicates'])) return root['duplicates'];
  if (Array.isArray(root['duplicate_files'])) return root['duplicate_files'];
  return [];
}

function extractPagination(
  raw: unknown,
  total: number,
  page: number,
  perPage: number,
): DuplicateFilesListResult['pagination'] {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const pagSource =
    root['pagination'] ??
    (root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)['pagination']
      : null);

  if (pagSource && typeof pagSource === 'object') {
    const pag = pagSource as Record<string, unknown>;
    const apiTotal = Number(pag['total'] ?? pag['total_records'] ?? total);
    const apiPerPage = Number(pag['per_page'] ?? pag['perPage'] ?? perPage) || perPage;
    const apiPage = Number(pag['page'] ?? pag['current_page'] ?? page) || page;
    const apiTotalPages = Number(
      pag['total_page'] ??
        pag['total_pages'] ??
        pag['last_page'] ??
        Math.ceil(apiTotal / apiPerPage),
    );

    return {
      page: apiPage,
      per_page: apiPerPage,
      total: apiTotal,
      total_page: Math.max(1, apiTotalPages),
    };
  }

  return {
    page,
    per_page: perPage,
    total,
    total_page: Math.max(1, Math.ceil(total / perPage)),
  };
}

export class DuplicateFilesService {
  static async listFiles(
    page = 1,
    perPage = 10,
    search = '',
  ): Promise<DuplicateFilesListResult> {
    const query = buildQueryString({
      page,
      per_page: perPage,
      search: search.trim(),
    });
    const endpoint = `${API_ENDPOINTS.LEADS_UPLOAD_DOWNLOAD_DUPLICATES}?${query}`;
    const raw = await apiFetch<unknown>(endpoint, 'GET');
    const rawRows = extractRows(raw);
    const files = rawRows
      .map((row, index) => normalizeRow(row, index))
      .filter((row): row is DuplicateFile => row !== null);

    const pagination = extractPagination(raw, files.length, page, perPage);

    if (pagination.total === files.length && files.length > perPage) {
      return {
        files,
        pagination: {
          page,
          per_page: perPage,
          total: files.length,
          total_page: Math.max(1, Math.ceil(files.length / perPage)),
        },
      };
    }

    return { files, pagination };
  }

  static async downloadFile(
    file: DuplicateFile,
    fallbackFilename = 'duplicates.csv',
  ): Promise<DuplicateFileDownloadResult> {
    const filename = file.filename || fallbackFilename;
    const blob = await LeadsExportService.downloadFromExportUrl(
      `${API_ENDPOINTS.LEADS_UPLOAD_DOWNLOAD_DUPLICATES}/${encodeURIComponent(filename)}`,
    );
    return { blob, filename };
  }

  static triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || 'duplicates.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }
}
